import { Component } from '@theme/component';

/**
 * Snug Whiskey Fest — Venue Profile Component
 *
 * Handles: photo gallery lightbox and Leaflet mini-map initialisation.
 * Event carousel is server-rendered (swf-event-card + swf-event-carousel.css).
 */
class SwfVenueProfileComponent extends Component {
  #galleryUrls = [];
  #lightboxIndex = 0;
  #map = null;

  connectedCallback() {
    super.connectedCallback();
    this.#loadGallery();
    this.#initMap();
    this.#bindEscKey();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#map) {
      this.#map.remove();
      this.#map = null;
    }
  }

  // ── Gallery / Lightbox ────────────────────────────────────────

  #loadGallery() {
    const script = this.querySelector('script[data-gallery]');
    if (!script) return;
    try {
      this.#galleryUrls = JSON.parse(script.textContent).filter(Boolean);
    } catch {
      // no gallery
    }
  }

  openLightbox(event) {
    const btn = event.target.closest('[data-index]');
    if (!btn) return;
    this.#lightboxIndex = parseInt(btn.dataset.index, 10) || 0;
    this.#showLightbox();
  }

  closeLightbox() {
    if (this.refs.lightbox) this.refs.lightbox.hidden = true;
    document.body.style.overflow = '';
  }

  prevImage() {
    this.#lightboxIndex = (this.#lightboxIndex - 1 + this.#galleryUrls.length) % this.#galleryUrls.length;
    this.#updateLightboxImage();
  }

  nextImage() {
    this.#lightboxIndex = (this.#lightboxIndex + 1) % this.#galleryUrls.length;
    this.#updateLightboxImage();
  }

  #showLightbox() {
    if (!this.refs.lightbox || this.#galleryUrls.length === 0) return;
    this.refs.lightbox.hidden = false;
    document.body.style.overflow = 'hidden';
    this.#updateLightboxImage();
  }

  #updateLightboxImage() {
    if (this.refs.lightboxImg) {
      this.refs.lightboxImg.src = this.#galleryUrls[this.#lightboxIndex] || '';
    }
  }

  #bindEscKey() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeLightbox();
      if (this.refs.lightbox && !this.refs.lightbox.hidden) {
        if (e.key === 'ArrowLeft') this.prevImage();
        if (e.key === 'ArrowRight') this.nextImage();
      }
    });
  }

  // ── Mini Map ──────────────────────────────────────────────────

  #initMap() {
    const mapEl = this.refs.map;
    if (!mapEl || typeof L === 'undefined') {
      // Leaflet not loaded yet — wait for it
      if (mapEl) {
        window.addEventListener('load', () => this.#initMap(), { once: true });
      }
      return;
    }

    const lat = parseFloat(mapEl.dataset.lat);
    const lng = parseFloat(mapEl.dataset.lng);
    if (isNaN(lat) || isNaN(lng)) return;

    this.#map = L.map(mapEl, {
      scrollWheelZoom: false,
      zoomControl: false,
      attributionControl: true,
    }).setView([lat, lng], 16);

    L.control.zoom({ position: 'bottomleft' }).addTo(this.#map);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.#map);

    // Gold marker
    const icon = L.divIcon({
      html: '<div style="width:20px;height:20px;background:#c8a96e;border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.4)"></div>',
      className: '',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

    L.marker([lat, lng], { icon }).addTo(this.#map);
  }
}

if (!customElements.get('swf-venue-profile-component')) {
  customElements.define('swf-venue-profile-component', SwfVenueProfileComponent);
}
